import { Fragment, useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { AppContext } from '../../../contexts/appContext';
import environment from '../../../utils/environment';
import { logInfo, logWarning } from '../../../utils/remoteLogger';

/**
 * FactoryLoginView — non-production helper that lets the kry-test-data-factory
 * hand the operator a single clickable URL.
 *
 * Usage: `/factory-login?session=<sessionid>&redirect=<path>`
 *
 * The factory (kry-test-data-generator) creates a demo patient and exchanges
 * the demo credentials for a sessionid via `POST /api/view/login/<market>/password/auth-code`.
 * Instead of asking the operator to paste personal-number/password/SMS-code
 * into the regular `/login?redirect=demo` flow, this route accepts the already-
 * minted session and feeds it straight into `AppContext.saveSession()`, which
 * sets the `sessionid` cookie and triggers an app reinit that lands on the
 * requested path.
 *
 * Hard-gated to non-production environments. The route does no network calls,
 * just promotes a session that the caller already obtained out-of-band.
 */
const FactoryLoginView = () => {
  const location = useLocation();
  const { saveSession } = useContext(AppContext);
  const processingRef = useRef(false);

  useEffect(() => {
    if (processingRef.current) return;
    processingRef.current = true;

    if (environment.IS_PROD) {
      logWarning(
        'FactoryLogin',
        'Auto-login route hit in production — ignoring'
      );
      window.location.replace('/');
      return;
    }

    const params = new URLSearchParams(location.search);
    const session = params.get('session');
    const rawRedirect = params.get('redirect') || '/';
    // Constrain to in-app relative paths. Reject `//`, `\\`, absolute URLs,
    // and anything else that could navigate off-origin.
    const redirect =
      rawRedirect.startsWith('/') && !rawRedirect.startsWith('//')
        ? rawRedirect
        : '/';

    if (!session) {
      logWarning(
        'FactoryLogin',
        'Missing ?session=<sid> — redirecting to /login'
      );
      window.location.replace('/login');
      return;
    }

    logInfo(
      'FactoryLogin',
      `Promoting factory session, redirecting to ${redirect}`
    );
    // saveSession sets the sessionid cookie, then (because reinitialize=true)
    // refreshes the app start data and navigates to `redirect`.
    saveSession(session, '', true, redirect);
  }, [location.search, saveSession]);

  return <Fragment />;
};

export default FactoryLoginView;
